import * as React from 'react'
import moment from 'moment'
import { duration as momentDuration } from 'moment'
import { ViolationModel } from '@/app/models/violationModel'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import { TableHeader } from '@/modules/drivers/shared/tableItems/TableHeader'
import TableTextRow from '@/modules/drivers/shared/tableItems/TableTextRow'
import TableEmpty from '@/modules/drivers/shared/tableItems/TableEmpty'
import { useSortableData } from '@/modules/drivers/hooks/useSortTable'
import { getViolationsColumns } from '@/modules/drivers/constants/tables'
import { format } from 'date-fns'

type Props = {
  violations: ViolationModel[]
  onViolationClick?: (violation: ViolationModel) => void
  confirmedViolations?: number[]
  onConfirmedViolationsChange?: (violations: number[]) => void
  companyId?: string
  driverId?: string
  history?: any
}

const ViolationList: React.FC<Props> = ({
  violations,
  onViolationClick,
  confirmedViolations,
  onConfirmedViolationsChange,
  companyId,
  driverId,
  history,
}) => {
  const { t } = useLocalize()
  const columns = getViolationsColumns(t)
  const [selectedIndex, setSelectedIndex] = React.useState<number | null>(null)

  const { sortedData, sortConfig, handleHeaderClick } = useSortableData({
    data: violations,
    tableColumns: columns,
  })

  React.useEffect(() => {
    if (violations.length > 0 && onConfirmedViolationsChange) {
      onConfirmedViolationsChange(violations.map((v) => v.id))
    }
  }, [violations.length])

  const formatDateTime = (dateStr: any): string => {
    if (!dateStr) return '-'
    try {
      const date = moment(dateStr)
      if (!date.isValid()) return '-'
      return date.format('DD.MM.YYYY HH:mm')
    } catch {
      return '-'
    }
  }

  const formatTimespan = (ts: any): string => {
    if (!ts) return '-'
    try {
      const duration = momentDuration(ts)
      const days = Math.floor(duration.asDays())
      const hours = duration.hours()
      const minutes = duration.minutes()

      let formatted = ''
      if (days > 0) formatted += `${days}d `
      if (hours > 0) formatted += `${hours}h `
      if (minutes > 0) formatted += `${minutes}m`

      return formatted.trim() || '-'
    } catch {
      return '-'
    }
  }

  const formatNumber = (value: number): string => {
    const [intPart, decimalPart] = value.toFixed(2).split('.')
    const formattedInt = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ' ')
    return `${formattedInt},${decimalPart} PLN`
  }

  const handleRowClick = (violation: ViolationModel, index: number) => {
    setSelectedIndex(index)
    if (onViolationClick) {
      onViolationClick(violation)
    }
  }

  const handleRowDoubleClick = (violation: ViolationModel) => {
    if (!violation.startTime || !companyId || !driverId || !history) return

    const violationDate = moment(violation.startTime)
    const dateStr = format(violationDate.toDate(), 'YYYY-MM-DD')

    const weeklyActivityPath = `/et/company/${companyId}/driverModule/activities/driver/${driverId}/weeklyActivity/${dateStr}`
    history.push(weeklyActivityPath)
  }

  const handleCheckboxChange = (violationId: number, checked: boolean) => {
    if (!onConfirmedViolationsChange || !confirmedViolations) return

    if (checked) {
      onConfirmedViolationsChange([...confirmedViolations, violationId])
    } else {
      onConfirmedViolationsChange(
        confirmedViolations.filter((id) => id !== violationId),
      )
    }
  }

  const toggleAll = (checked: boolean) => {
    if (!onConfirmedViolationsChange) return
    if (checked) {
      onConfirmedViolationsChange(violations.map((v) => v.id))
    } else {
      onConfirmedViolationsChange([])
    }
  }

  const isSelectedAny = confirmedViolations && confirmedViolations.length > 0
  const allChecked =
    confirmedViolations &&
    violations.length > 0 &&
    confirmedViolations.length === violations.length

  return (
    <section className="tw-h-full tw-overflow-x-auto">
      <div className="tw-relative tw-h-full">
        {sortedData.length > 0 ? (
          <table className="tw-relative tw-w-full tw-table-auto tw-border-spacing-x-2">
            <TableHeader
              opacity
              rounded={false}
              toggleAll={toggleAll}
              isSelectedAny={!!isSelectedAny}
              columns={columns}
              sortConfig={sortConfig}
              onHeaderClick={handleHeaderClick}
              disabledToggle={!onConfirmedViolationsChange}
            />
            <tbody>
              {sortedData.map((violation, index) => {
                const isChecked =
                  confirmedViolations?.includes(violation.id) || false
                const isSelected = selectedIndex === index

                return (
                  <tr
                    key={violation.id}
                    className={`notches-border--right tw-w-fit tw-cursor-pointer tw-border-b tw-bg-white hover:tw-bg-primary/10 ${
                      isSelected ? 'tw-bg-[#E0F0F0]' : ''
                    }`}
                    onClick={() => handleRowClick(violation, index)}
                    onDoubleClick={() => handleRowDoubleClick(violation)}
                  >
                    {onConfirmedViolationsChange && (
                      <td
                        className="stickyChild notches-border--left tw-relative tw-w-4 tw-bg-white tw-pb-1.5 tw-pl-2 tw-pr-2 tw-pt-2.5"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <label className="tw-inline-flex tw-items-center">
                          <input
                            type="checkbox"
                            checked={isChecked}
                            onChange={(e) =>
                              handleCheckboxChange(
                                violation.id,
                                e.target.checked,
                              )
                            }
                            className="tw-cursor-pointer"
                          />
                        </label>
                      </td>
                    )}
                    <TableTextRow>
                      {formatDateTime(violation.startTime)}
                    </TableTextRow>
                    <TableTextRow>
                      {formatDateTime(violation.endTime)}
                    </TableTextRow>
                    <TableTextRow>
                      {formatTimespan(violation.exceededBy)}
                    </TableTextRow>
                    <TableTextRow>
                      {violation.description &&
                      typeof violation.description === 'string'
                        ? t(violation.description)
                        : violation.description || '-'}
                    </TableTextRow>
                    <TableTextRow>{violation.weight}</TableTextRow>
                    <TableTextRow>{violation.chargeSymbol}</TableTextRow>
                    <TableTextRow>
                      {formatNumber(violation.driverCharge)}
                    </TableTextRow>
                    <TableTextRow>
                      {formatNumber(violation.companyCharge)}
                    </TableTextRow>
                  </tr>
                )
              })}
            </tbody>
          </table>
        ) : (
          <>
            <TableHeader
              opacity
              toggleAll={toggleAll}
              isSelectedAny={!!isSelectedAny}
              columns={columns}
              sortConfig={sortConfig}
              disabledToggle={!onConfirmedViolationsChange}
            />
            <TableEmpty title={t('driverViolations.no-data')} />
          </>
        )}
      </div>
    </section>
  )
}

export default ViolationList
